import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/solid-router";
import { createEffect, createSignal, For } from "solid-js";
import {
  useCreateProjectMutation,
  useProjectModulesQuery,
} from "~/lib/queries/projects";

export const Route = createFileRoute("/_authenticated/projects/new")({
  ssr: false,
  component: NewProjectPage,
});

function NewProjectForm(props: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [nameError, setNameError] = createSignal("");
  const [selectedModules, setSelectedModules] = createSignal<string[]>([]);

  const mutation = useCreateProjectMutation();
  const modulesQuery = useProjectModulesQuery();

  createEffect(() => {
    const modules = modulesQuery.data;
    if (!modules || modules.length === 0) {
      return;
    }
    if (selectedModules().length > 0) {
      return;
    }
    const defaults = modules.filter((m) => m.isDefault).map((m) => m.id);
    setSelectedModules(defaults);
  });

  const toggleModule = (moduleId: string, checked: boolean) => {
    setSelectedModules((current) => {
      if (checked) {
        if (current.includes(moduleId)) {
          return current;
        }
        return [...current, moduleId];
      }
      return current.filter((id) => id !== moduleId);
    });
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!name().trim()) {
      setNameError("Project name is required");
      return;
    }
    setNameError("");
    await mutation.mutateAsync({
      name: name().trim(),
      description: description().trim(),
      modules: selectedModules(),
    });
    props.onSuccess();
  };

  const inputClass =
    "w-full h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground " +
    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ring transition-colors";

  const labelClass = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <form onSubmit={handleSubmit} class="space-y-5">
      <div>
        <label for="project-name" class={labelClass}>
          Project name{" "}
          <span class="text-destructive" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="project-name"
          type="text"
          placeholder="My Genealogy Project"
          value={name()}
          onInput={(e) => setName(e.currentTarget.value)}
          class={`${inputClass} ${nameError() ? "border-destructive focus-visible:ring-destructive/50" : ""}`}
          autocomplete="off"
        />
        {nameError() && (
          <p class="mt-1.5 text-xs text-destructive">{nameError()}</p>
        )}
      </div>

      <div>
        <label for="project-description" class={labelClass}>
          Description{" "}
          <span class="text-muted-foreground text-xs font-normal">
            (optional)
          </span>
        </label>
        <textarea
          id="project-description"
          rows="3"
          placeholder="A brief description of this project…"
          value={description()}
          onInput={(e) => setDescription(e.currentTarget.value)}
          class={`${inputClass} h-auto py-2 resize-none`}
        />
      </div>

      <div>
        <p class={labelClass}>Modules</p>
        {modulesQuery.isLoading && (
          <p class="text-xs text-muted-foreground">Loading modules...</p>
        )}
        {modulesQuery.isError && (
          <p class="text-xs text-destructive">
            Failed to load modules. You can still create the project.
          </p>
        )}
        {modulesQuery.data && modulesQuery.data.length > 0 && (
          <div class="space-y-2 rounded-lg border border-border p-3">
            <For each={modulesQuery.data}>
              {(module) => (
                <label class="flex items-start gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={selectedModules().includes(module.id)}
                    onChange={(e) =>
                      toggleModule(module.id, e.currentTarget.checked)
                    }
                    class="mt-0.5"
                  />
                  <span>
                    <span class="font-medium">{module.name || module.id}</span>
                    {module.isDefault && (
                      <span class="ml-2 text-xs text-muted-foreground">
                        default
                      </span>
                    )}
                    {module.description && (
                      <span class="block text-xs text-muted-foreground">
                        {module.description}
                      </span>
                    )}
                  </span>
                </label>
              )}
            </For>
          </div>
        )}
      </div>

      {mutation.isError && (
        <p class="text-xs text-destructive">
          Failed to create project. Please try again.
        </p>
      )}

      <div class="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={props.onCancel}
          disabled={mutation.isPending}
          class="h-9 px-4 rounded-lg border border-border bg-transparent text-sm font-medium
                 text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          class="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium
                 hover:bg-primary/90 transition-colors disabled:opacity-70
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {mutation.isPending ? "Creating…" : "Create project"}
        </button>
      </div>
    </form>
  );
}

function NewProjectPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Detect if this route is being rendered inside a masked navigation
  const isMasked = () => !!location().maskedLocation;

  const onSuccess = () => navigate({ to: "/projects", replace: true });
  const onCancel = () => navigate({ to: "/projects", replace: true });

  // When rendered as a modal (route masking), show a dialog overlay
  if (isMasked()) {
    return (
      <>
        {/* Backdrop */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Backdrop should dismiss dialog */}
        <div
          class="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={onCancel}
          role="presentation"
        />
        {/* Dialog */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-project-title"
          class="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2
                 w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl
                 animate-in fade-in zoom-in-95 duration-150"
        >
          <h2
            id="new-project-title"
            class="text-base font-semibold text-foreground mb-5"
          >
            New Project
          </h2>
          <NewProjectForm onSuccess={onSuccess} onCancel={onCancel} />
        </div>
      </>
    );
  }

  // Full-page fallback (direct URL access / reload)
  return (
    <div class="max-w-lg mx-auto">
      <h1 class="text-xl font-semibold text-foreground mb-6">New Project</h1>
      <div class="rounded-xl border border-border bg-card p-6">
        <NewProjectForm onSuccess={onSuccess} onCancel={onCancel} />
      </div>
    </div>
  );
}
